/**
 * NarrativeEventCapture — type or paste a free-text narrative (or a spreadsheet)
 * describing one or many farm events; the backend (AI when available, else a
 * deterministic parser) extracts structured candidate events. The user reviews
 * /edits/removes the candidates, then commits them to the activity feed.
 *
 * Used in two places: the Activity page "Log Event" modal (Describe/paste mode)
 * and the onboarding wizard's "Record your farm history" step (backdated events).
 *
 * Two-step: activityApi.parse (preview, no write) → activityApi.commit (persist).
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Trash2, Plus, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react'
import { activityApi, type ParsedEvent } from '../../lib/api'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', border: 'rgba(255,255,255,0.09)',
  amber: 'var(--color-primary)', green: '#2D6A2F', greenLt: '#34D399', red: '#F87171',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}

// Mirrors backend validActionTypes (internal/activity/parse.go) + ActivityPage labels.
const ACTION_TYPES = [
  'observation', 'harvest', 'fertilize', 'spray', 'irrigate',
  'prune', 'graft', 'scion_push', 'soil_test', 'train', 'thin', 'custom',
] as const

function isoToDateInput(iso: string): string {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}
function dateInputToIso(dateStr: string): string {
  // Treat the picked calendar day as noon UTC to avoid TZ off-by-one.
  const d = new Date(dateStr + 'T12:00:00Z')
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
}

interface Props {
  referenceDate?: string          // anchors relative dates; defaults to today server-side
  onCommitted?: (count: number) => void
  placeholder?: string
}

export function NarrativeEventCapture({ referenceDate, onCommitted, placeholder }: Props) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [events, setEvents] = useState<ParsedEvent[] | null>(null)
  const [aiUsed, setAiUsed] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [committedCount, setCommittedCount] = useState<number | null>(null)

  const parse = useMutation({
    mutationFn: () => activityApi.parse(text, referenceDate).then(r => r.data),
    onSuccess: (data) => {
      setEvents(data.events)
      setAiUsed(data.ai_used)
      setWarnings(data.warnings ?? [])
    },
  })

  const commit = useMutation({
    mutationFn: () => activityApi.commit(events ?? []).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['activity'] })
      setCommittedCount(data.created)
      setEvents(null)
      setText('')
      onCommitted?.(data.created)
      setTimeout(() => setCommittedCount(null), 4000)
    },
  })

  const patch = (i: number, p: Partial<ParsedEvent>) =>
    setEvents(evs => (evs ?? []).map((e, idx) => idx === i ? { ...e, ...p } : e))
  const remove = (i: number) =>
    setEvents(evs => (evs ?? []).filter((_, idx) => idx !== i))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Input */}
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={placeholder ?? 'Describe what happened — one event or a whole day. e.g. "Sprayed copper on the Kent block Tuesday, picked 180 lbs of Haden today, fertilized block 3 last week." You can also paste a spreadsheet.'}
        style={{
          width: '100%', boxSizing: 'border-box', minHeight: 96, resize: 'vertical',
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '10px 12px', color: C.text, fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5,
        }}
      />

      {!events && (
        <button
          onClick={() => parse.mutate()}
          disabled={!text.trim() || parse.isPending}
          style={primaryBtn(!text.trim() || parse.isPending)}
        >
          {parse.isPending ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
          {parse.isPending ? 'Reading…' : 'Extract events'}
        </button>
      )}

      {parse.isError && (
        <Banner color={C.red}><AlertTriangle size={14} /> Couldn't read that — try rephrasing, or check your connection.</Banner>
      )}

      {committedCount !== null && (
        <Banner color={C.greenLt}><CheckCircle2 size={14} /> Added {committedCount} {committedCount === 1 ? 'event' : 'events'} to your activity.</Banner>
      )}

      {/* Candidate review */}
      {events && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 12, color: C.muted }}>
              Found <strong style={{ color: C.text }}>{events.length}</strong> {events.length === 1 ? 'event' : 'events'} — review, edit, or remove before adding.
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100,
              color: aiUsed ? C.amber : C.faint,
              background: aiUsed ? 'color-mix(in srgb, var(--color-primary) 9%, transparent)' : 'var(--color-bg-hover)',
              border: `1px solid ${aiUsed ? 'color-mix(in srgb, var(--color-primary) 20%, transparent)' : C.border}`,
            }}>
              {aiUsed ? 'auto' : 'basic'}
            </span>
          </div>

          {warnings.map((w, i) => (
            <Banner key={i} color={C.faint}><AlertTriangle size={13} /> {w}</Banner>
          ))}

          {events.length === 0 ? (
            <p style={{ fontSize: 12, color: C.faint, margin: 0 }}>No events detected. Edit your text and try again.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {events.map((e, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 130px 1fr 28px', gap: 8, alignItems: 'center' }}>
                    <select value={e.action_type} onChange={ev => patch(i, { action_type: ev.target.value })} style={inp}>
                      {ACTION_TYPES.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
                    </select>
                    <input type="date" value={isoToDateInput(e.occurred_at)} onChange={ev => patch(i, { occurred_at: dateInputToIso(ev.target.value) })} style={inp} />
                    <input value={e.description} onChange={ev => patch(i, { description: ev.target.value })} placeholder="Description" style={inp} />
                    <Trash2 size={15} style={{ cursor: 'pointer', color: C.faint, justifySelf: 'center' }} onClick={() => remove(i)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, marginTop: 8 }}>
                    <input value={e.resource_label ?? ''} onChange={ev => patch(i, { resource_label: ev.target.value })} placeholder="Block / zone / tree (optional)" style={inp} />
                    <input type="number" value={e.quantity ?? ''} onChange={ev => patch(i, { quantity: ev.target.value ? Number(ev.target.value) : null })} placeholder="Qty" style={inp} />
                    <input value={e.unit ?? ''} onChange={ev => patch(i, { unit: ev.target.value })} placeholder="Unit (lb…)" style={inp} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => commit.mutate()} disabled={events.length === 0 || commit.isPending} style={primaryBtn(events.length === 0 || commit.isPending)}>
              {commit.isPending ? <Loader2 size={15} className="spin" /> : <Plus size={15} />}
              {commit.isPending ? 'Adding…' : `Add ${events.length} ${events.length === 1 ? 'event' : 'events'}`}
            </button>
            <button onClick={() => { setEvents(null); setWarnings([]) }} style={ghostBtn}>Back</button>
          </div>
          {commit.isError && <Banner color={C.red}><AlertTriangle size={14} /> Couldn't save — try again.</Banner>}
        </>
      )}
    </div>
  )
}

function Banner({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color,
      background: color + '11', border: `1px solid ${color}33`, borderRadius: 8, padding: '7px 10px', lineHeight: 1.4,
    }}>{children}</div>
  )
}

const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 8px', fontSize: 12, outline: 'none',
}
const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
  background: disabled ? C.green + '88' : C.green, color: '#fff', border: 'none',
  borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
})
const ghostBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', color: C.muted,
  border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer',
}

export default NarrativeEventCapture
